// Settings
import React, { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import styles from './Settings.module.sass';
import SettingsForm from './SettingsForm';
import {
  SettingsFormikValues,
  SettingsTabId,
  SettingsValidationSchema,
  SETTINGS_TABS,
} from 'constants/settings';
import cn from 'classnames';
import Dropdown from 'components/common/Dropdown';
import useSWR, { useSWRConfig } from 'swr';
import { SettingsResponse } from 'types/profile';
import Loader from 'components/common/Loader';
import { patchCreatorProfile } from 'api/creator';
import { useAuth } from 'hooks/useAuth';
import { trendSWRFetch } from 'api';
import { getKeysWithUpdatedValues } from 'utils/object';
import useToast from 'components/common/Toast';
import { trackEvent } from 'services/analytics';

export interface SettingsProps {}

const Settings: React.FC<SettingsProps> = ({}) => {
  const { mutate } = useSWRConfig();
  const { triggerToast } = useToast();
  const { data } = useSWR<SettingsResponse>(
    '/creator/profile/settings',
    trendSWRFetch
  );
  const [selectedSection, setSection] = useState<SettingsTabId>('basics');
  const { authProfile } = useAuth();
  const { creator } = authProfile || {}; // TODO: Implement mock for useAuth so `user!` works for storybook
  const { jobTypes: _j, address: _a, ...settingsFormValues } = data || {};

  const handleSubmit = async (
    values: SettingsFormikValues,
    _helpers: FormikHelpers<SettingsFormikValues>
  ) => {
    const updatedKeys = getKeysWithUpdatedValues(values, data!);
    const updatedValues = updatedKeys.reduce(
      (prevValue, key) => ({
        ...prevValue,
        [key]: values[key as keyof SettingsFormikValues],
      }),
      {}
    );

    return patchCreatorProfile(creator!._id, updatedValues)
      .then((res) => {
        if (res.ok) {
          trackEvent(`Creator Saves Settings Updates`, {
            updatedValues,
          });
          // TODO: Check return; Update profiles cache
          mutate('/creator/profile');
          triggerToast('Profile Updated');
        }
      })
      .catch((err) => {
        console.error(err);
        triggerToast('Error Updating Profile');
      });
  };

  const onMenuClick = (tab: SettingsTabId) => {
    setSection(tab);
    const element = document.getElementById(tab);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start',
      });
      trackEvent('Creator Clicks Settings Menu Item', { menuItem: tab });
    }
  };

  return (
    <div className={styles.settings}>
      <div className={styles.mobileHeader}>
        <h4>Settings</h4>
      </div>
      <div className={styles.card}>
        {typeof data !== 'undefined' ? (
          <>
            <div className={styles.menu}>
              <Dropdown
                options={SETTINGS_TABS}
                value={selectedSection}
                setValue={onMenuClick}
                className={styles.dropdown}
              />
              <div className={styles.sideMenu}>
                {SETTINGS_TABS.map(({ label, value }) => (
                  <div
                    key={value}
                    onClick={() => onMenuClick(value)}
                    className={cn(styles.tabLink, {
                      [styles.selected]: selectedSection === value,
                    })}
                  >
                    {label}
                  </div>
                ))}
              </div>
            </div>
            <Formik
              initialValues={settingsFormValues as SettingsFormikValues}
              validationSchema={SettingsValidationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              <SettingsForm />
            </Formik>
          </>
        ) : (
          <div className={styles.loader}>
            <Loader />
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
